/**
 * Agents Route
 * Lists and manages workspace AI agents
 */

import { TopBarRoute, ListView } from "../../components/templates";
import {
  Stack,
  Title,
  Card,
  Icon,
  useToast,
  Modal,
  Drawer,
  Tag
} from "@introist/react-foundation/v2";
import { useAgents, useAgentCatalog } from "../hooks/use-agents";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { agentsApi, AgentManifest } from "../api";
import { Skeletons } from "../../components/molecules";
import { useState } from "react";
import { List, ListRow } from "../../components/organisms";
import { useParams, useNavigate } from "react-router-dom";
import { AgentEditor } from "../agent-editor/AgentEditor";

export const AgentsRoute = () => {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const { agentId } = useParams<{ agentId?: string }>();
  const navigate = useNavigate();

  return (
    <TopBarRoute
      paths={[
        {
          key: "agents",
          title: "Agents"
        }
      ]}
    >
      <Stack vertical>
        <ListView.Header title="Agents" onCreate={() => setCreateModalOpen(true)} />
        <AgentsListContent />
        <CreateAgentModal open={createModalOpen} onClose={() => setCreateModalOpen(false)} />
      </Stack>

      {/* Configured Agent Editor Drawer */}
      <Drawer open={!!agentId} onClose={() => navigate("/aistudio/agents")} width="540px">
        {agentId && <AgentEditor agentId={agentId} onClose={() => navigate("/aistudio/agents")} />}
      </Drawer>
    </TopBarRoute>
  );
};

const AgentsListContent = () => {
  const { data: agents = [], isLoading } = useAgents();
  const navigate = useNavigate();

  if (isLoading) {
    return <Skeletons.List rowCount={3} rowHeight={60} />;
  }

  if (agents.length === 0) {
    return (
      <Card>
        <Stack
          vertical
          style={{
            alignItems: "center",
            gap: "var(--spacing-small)",
            padding: "var(--spacing-large)"
          }}
        >
          <Icon name="robot" dimmed />
          <Title style={{ color: "var(--palette-foreground-subdued)" }}>
            No agents configured yet
          </Title>
          <Title small style={{ color: "var(--palette-foreground-subdued)" }}>
            Add an agent to automate HR tasks
          </Title>
        </Stack>
      </Card>
    );
  }

  return (
    <List>
      {agents.map(agent => (
        <ListRow
          key={agent.id}
          gridTemplateColumns="auto 1fr auto"
          onClick={() => navigate(`/aistudio/agents/${agent.id}`)}
          clickable
        >
          <div
            style={{
              width: "24px",
              height: "24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <Icon name="robot" />
          </div>
          <Title variant="bold">{agent.name}</Title>
          <Stack>
            <Tag colorVariant={agent.is_active ? "success" : "outlined"}>
              {agent.is_active ? "Active" : "Inactive"}
            </Tag>
          </Stack>
        </ListRow>
      ))}
    </List>
  );
};

interface CreateAgentModalProps {
  open: boolean;
  onClose: () => void;
}

const CreateAgentModal = ({ open, onClose }: CreateAgentModalProps) => {
  const { data: catalog = [], isLoading } = useAgentCatalog();
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();

  const createMutation = useMutation({
    mutationFn: async (agentType: string) => {
      const manifest = catalog.find(a => a.type === agentType)!;
      return agentsApi.create({ type: agentType, name: manifest.name });
    },
    onSuccess: async newAgent => {
      await queryClient.invalidateQueries({ queryKey: ["ai-studio", "agents", "configured"] });
      toast.success("Agent created successfully");
      onClose();
      navigate(`/aistudio/agents/${newAgent.id}`);
    },
    onError: (error: any) => {
      toast.error(`Failed to create agent: ${error.message}`);
    }
  });

  const handleSelectAgent = async (agentType: string) => {
    await createMutation.mutateAsync(agentType);
  };

  return (
    <Modal open={open} onClose={onClose} title="Add Agent">
      <Stack vertical style={{ width: "400px" }}>
        <Title small style={{ color: "var(--palette-foreground-subdued)" }}>
          Select an agent type.
        </Title>

        {isLoading ? (
          <Skeletons.List rowCount={3} rowHeight={80} />
        ) : (
          <Stack vertical style={{ gap: "var(--spacing-small)" }}>
            {catalog.map((agentManifest: AgentManifest) => (
              <Card
                key={agentManifest.type}
                onClick={() => handleSelectAgent(agentManifest.type)}
                style={{ cursor: "pointer" }}
              >
                <Stack style={{ gap: "var(--spacing-medium)", alignItems: "center" }}>
                  <div
                    style={{
                      width: "24px",
                      height: "24px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    <Icon name="robot" />
                  </div>
                  <Stack vertical style={{ gap: "var(--spacing-xxSmall)", flex: 1 }}>
                    <Title variant="bold">{agentManifest.name}</Title>
                    <Title small style={{ color: "var(--palette-foreground-subdued)" }}>
                      {agentManifest.description}
                    </Title>
                  </Stack>
                  <Icon name="chevronRight" dimmed />
                </Stack>
              </Card>
            ))}
          </Stack>
        )}
      </Stack>
    </Modal>
  );
};
